import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowDown, ArrowUp, ImageIcon, Search } from 'lucide-react';

import { cn } from '@/lib/cn';
import {
  reviewPolicyOf,
  storefrontBucket,
  useAdminStorefronts,
  type AdminStorefrontListItem,
  type StorefrontListFilter,
  type StorefrontListTab,
  type StorefrontReviewPolicy,
} from '@/lib/api/storefrontAdminHooks';

import { REVIEW_POLICIES, ReviewPolicyPill, isReviewPolicy } from './reviewPolicy';

import { editedSinceApproval as isEditedSinceApproval, liveButNotServed, storeProblems } from './storeCompliance';
import {
  REVIEW_LABEL,
  REVIEW_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  STORE_ROOT_DOMAIN,
  ageLabel,
  count,
  shortDateTime,
  waitTone,
} from './storefrontFormat';
import { ADMIN_CONTRAST, Notice, Pill, Skeleton, StoreMark, Td, Th } from './ui';

/** A list tab: one count bucket, every store, or the stores whose owner
 *  published without a review since the last one (a view across the Live
 *  and Paused buckets, not a bucket of its own). */
type ScreenTab = StorefrontListFilter | 'published_unreviewed';

// "All" has no count (the counts cover the six review buckets); it lists
// every store, so search can find one whatever its state.
const TABS: { key: ScreenTab; label: string }[] = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'changes_pending_review', label: 'Changes pending' },
  { key: 'published_unreviewed', label: 'Published without review' },
  { key: 'changes_requested', label: 'Changes requested' },
  { key: 'live', label: 'Live' },
  { key: 'paused', label: 'Paused' },
  { key: 'draft', label: 'Drafts' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

/** Tabs whose count is work waiting on an admin (highlighted). */
const QUEUE_TABS = new Set<ScreenTab>(['submitted', 'changes_pending_review', 'published_unreviewed']);

const EMPTY_COPY: Record<ScreenTab, string> = {
  published_unreviewed:
    'Every owner publish since the last review has been looked at. Live stores whose owners publish without a review (colours, layout, unchanged copy) appear here until someone marks them reviewed.',
  submitted: 'Nothing waiting for review.',
  changes_pending_review:
    'No published changes waiting. Live stores whose owners publish flagged copy or new photos appear here.',
  changes_requested: 'No store is waiting on its owner.',
  live: 'No store is live.',
  paused: 'No paused stores.',
  draft: 'No drafts. Stores appear here when an owner redeems an invite.',
  rejected: 'No rejected stores.',
  all: 'No stores yet.',
};

function isTab(value: string | null): value is ScreenTab {
  return TABS.some((tab) => tab.key === value);
}

/** Stores | Invites, shared by both screens. */
export function StorefrontsHeader({ description }: { description: string }) {
  return (
    <div className="flex flex-col gap-16">
      <div className="flex flex-col gap-8">
        <span className="type-label-xs text-ink-muted">Operations · rx-store</span>
        <h1 className="type-headline-lg text-ink">Storefronts</h1>
        <p className="type-body-md text-ink-secondary max-w-[68ch] leading-130">{description}</p>
      </div>
      <nav className="flex items-center gap-0 border-b border-line-subtle">
        {[
          { to: '/admin/storefronts', label: 'Stores', end: true },
          { to: '/admin/storefronts/invites', label: 'Owner invites', end: false },
        ].map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                '-mb-px h-40 px-14 flex items-center type-label-sm border-b-2',
                isActive
                  ? 'border-ink text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink',
              )
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

type SortKey = 'name' | 'products' | 'customers' | 'submitted_at' | 'reviewed_at' | 'updated_at';
type Sort = { key: SortKey; dir: 'asc' | 'desc' } | null;

function sortValue(row: AdminStorefrontListItem, key: SortKey): string | number {
  if (key === 'name') return row.name.toLowerCase();
  if (key === 'products') return row.products_count;
  if (key === 'customers') return row.customers_count ?? 0;
  const value = row[key];
  return value ? Date.parse(value) || 0 : 0;
}

function sortRows(rows: AdminStorefrontListItem[], sort: Sort): AdminStorefrontListItem[] {
  if (!sort) return rows;
  const factor = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = sortValue(a, sort.key);
    const right = sortValue(b, sort.key);
    // Rows without a date sort last either way.
    if (left === 0 && right !== 0) return 1;
    if (right === 0 && left !== 0) return -1;
    return left < right ? -factor : left > right ? factor : 0;
  });
}

const publishedUnreviewed = (row: AdminStorefrontListItem) => (row.published_since_review ?? 0) > 0;

/** A store with public content (live or paused) publishes under its review
 *  policy; before its first approval the policy is not chosen yet. */
const hasPolicy = (row: AdminStorefrontListItem) => row.status === 'live' || row.status === 'paused';

function inTab(row: AdminStorefrontListItem, tab: ScreenTab): boolean {
  if (tab === 'published_unreviewed') return publishedUnreviewed(row);
  return tab === 'all' || storefrontBucket(row) === tab;
}

/** Newest unreviewed publish first (the backend's order for this view). */
function byLastPublished(rows: AdminStorefrontListItem[]): AdminStorefrontListItem[] {
  return [...rows].sort(
    (a, b) => (Date.parse(b.last_published_at ?? '') || 0) - (Date.parse(a.last_published_at ?? '') || 0),
  );
}

export function StorefrontsScreen() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: ScreenTab = isTab(tabParam) ? tabParam : 'submitted';
  const [q, setQ] = useState(params.get('q') ?? '');
  const [debouncedQ, setDebouncedQ] = useState(q);
  // Review policy filter (`?policy=`): the live and paused stores under one
  // policy, across every tab.
  const policyParam = params.get('policy');
  const policy: StorefrontReviewPolicy | null = isReviewPolicy(policyParam) ? policyParam : null;
  // Searching or filtering: each tab shows how many of its stores match.
  const narrowed = Boolean(debouncedQ || policy);
  // The review queue reads oldest first; other tabs keep the backend's
  // newest-first order until a column is clicked.
  const [sort, setSort] = useState<Sort>(null);
  const effectiveSort: Sort =
    sort ?? (tab === 'submitted' ? { key: 'submitted_at', dir: 'asc' } : null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedQ) next.set('q', debouncedQ);
    else next.delete('q');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  const { data, isLoading, isError, error, isFetching } = useAdminStorefronts(debouncedQ);
  // The selected tab stays in view on a narrow window (the tab row scrolls).
  const activeTabRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    activeTabRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [tab]);
  const all = useMemo(
    () =>
      (data?.items ?? []).filter(
        (row) => !policy || (hasPolicy(row) && reviewPolicyOf(row) === policy),
      ),
    [data?.items, policy],
  );
  const items = useMemo(() => {
    const rows = all.filter((row) => inTab(row, tab));
    return tab === 'published_unreviewed' && !effectiveSort
      ? byLastPublished(rows)
      : sortRows(rows, effectiveSort);
  }, [all, tab, effectiveSort]);
  // While searching, each tab shows how many results it holds.
  const matchCounts = useMemo(() => {
    const out: Partial<Record<StorefrontListTab | 'published_unreviewed', number>> = {};
    for (const row of all) {
      const bucket = storefrontBucket(row);
      if (bucket) out[bucket] = (out[bucket] ?? 0) + 1;
      if (publishedUnreviewed(row)) out.published_unreviewed = (out.published_unreviewed ?? 0) + 1;
    }
    return out;
  }, [all]);
  const otherTabMatches = narrowed
    ? TABS.filter(
        (t) =>
          t.key !== tab &&
          t.key !== 'all' &&
          t.key !== 'published_unreviewed' &&
          (matchCounts[t.key as StorefrontListTab] ?? 0) > 0,
      )
    : [];

  function selectPolicy(next: string) {
    const nextParams = new URLSearchParams(params);
    if (isReviewPolicy(next)) nextParams.set('policy', next);
    else nextParams.delete('policy');
    setParams(nextParams, { replace: true });
  }

  function selectTab(next: ScreenTab) {
    const nextParams = new URLSearchParams(params);
    nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
    setSort(null);
  }

  function toggleSort(key: SortKey) {
    setSort((current) => {
      const base = current ?? effectiveSort;
      if (base?.key === key) return { key, dir: base.dir === 'asc' ? 'desc' : 'asc' };
      return { key, dir: key === 'name' ? 'asc' : 'desc' };
    });
  }

  return (
    <div className="flex flex-col gap-24" style={ADMIN_CONTRAST}>
      <StorefrontsHeader description="Creator storefronts on our backend. Review what owners submit, set stores live or pause them, and record manual payouts. Owners never see patient names, contact details or medical information; neither does this screen." />

      <div className="flex flex-col gap-0 border border-line-subtle bg-page">
        {/* Search on its own row and the tabs under it, full width: eight
            tabs do not fit beside the search box from a 1280px window. */}
        <div className="flex flex-col gap-10 px-12 pt-10 sm:flex-row sm:items-center sm:justify-between">
          <span className="type-body-sm text-ink-muted">
            {debouncedQ
              ? `Searching every tab for “${debouncedQ}”${policy ? ' (one review policy)' : ''}`
              : policy
                ? 'Live and paused stores under one review policy, in every tab.'
                : 'Review queue first: Submitted, then Changes pending.'}
          </span>
          <div className="flex flex-col gap-8 sm:flex-row sm:items-center sm:shrink-0">
            <label className="flex items-center h-34 border border-line bg-page focus-within:border-ink">
              <span className="pl-10 pr-6 type-body-sm text-ink-muted whitespace-nowrap">
                Review policy
              </span>
              <select
                value={policy ?? ''}
                onChange={(e) => selectPolicy(e.target.value)}
                className="h-full min-w-0 flex-1 pl-4 pr-8 bg-transparent type-body-sm text-ink outline-none cursor-pointer"
              >
                <option value="">Any</option>
                {REVIEW_POLICIES.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="relative sm:w-[28rem] sm:shrink-0">
              <span className="sr-only">Search every store</span>
              <Search
                size={14}
                aria-hidden
                className="absolute left-10 top-1/2 -translate-y-1/2 text-ink-muted"
              />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search every store: name, slug or owner"
                className="w-full h-34 pl-30 pr-10 border border-line bg-page type-body-sm outline-none focus:border-ink placeholder:text-ink-muted"
              />
            </label>
          </div>
        </div>
        <div className="px-12 border-b border-line-subtle">
          <div
            role="tablist"
            aria-label="Review state"
            className="flex items-end -mb-px overflow-x-auto -mx-12 px-12 pt-6"
          >
            {TABS.map((item) => {
              const active = item.key === tab;
              const n =
                item.key === 'all'
                  ? narrowed
                    ? all.length
                    : undefined
                  : narrowed
                    ? (matchCounts[item.key] ?? 0)
                    : item.key === 'published_unreviewed'
                      ? (data?.publishedUnreviewed ?? matchCounts.published_unreviewed)
                      : data?.counts[item.key];
              const queue = QUEUE_TABS.has(item.key) && Boolean(n);
              return (
                <button
                  key={item.key}
                  ref={active ? activeTabRef : undefined}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => selectTab(item.key)}
                  className={cn(
                    'h-44 px-12 flex items-center gap-8 type-label-sm border-b-2 whitespace-nowrap shrink-0',
                    active
                      ? 'border-ink text-ink'
                      : 'border-transparent text-ink-muted hover:text-ink',
                  )}
                >
                  {item.label}
                  {(item.key !== 'all' || narrowed) && (
                    <span
                      className={cn(
                        'min-w-20 h-18 px-5 inline-flex items-center justify-center type-label-xs tabular-nums',
                        active
                          ? queue
                            ? 'bg-info text-white'
                            : 'bg-ink text-white'
                          : queue
                            ? 'bg-info-wash text-info'
                            : 'bg-sunken text-ink-muted',
                      )}
                      title={narrowed ? 'Stores matching your search or filter' : undefined}
                    >
                      {n == null ? '–' : n}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {isError ? (
          <div className="p-16">
            <Notice tone="danger">
              Could not load storefronts. {(error as Error | null)?.message ?? ''}
            </Notice>
          </div>
        ) : isLoading ? (
          <div className="flex flex-col">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-12 px-12 py-12 border-b border-line-subtle last:border-b-0">
                <Skeleton className="w-36 h-36" />
                <Skeleton className="h-12 w-[18rem]" />
                <Skeleton className="h-12 w-[10rem] ml-auto" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="px-16 py-40 flex flex-col items-center gap-12 text-center">
            <p className="type-body-md text-ink-muted">
              {debouncedQ
                ? `No stores in ${TABS.find((t) => t.key === tab)?.label ?? 'this tab'} match “${debouncedQ}”${policy ? ' under this review policy' : ''}.`
                : policy
                  ? `No ${TABS.find((t) => t.key === tab)?.label.toLowerCase() ?? ''} stores use the “${REVIEW_POLICIES.find((p) => p.key === policy)?.label}” review policy.`
                  : EMPTY_COPY[tab]}
            </p>
            {otherTabMatches.length > 0 && (
              <div className="flex flex-wrap justify-center gap-8">
                {otherTabMatches.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => selectTab(t.key)}
                    className="h-30 px-10 border border-line type-label-xs text-ink hover:bg-sunken"
                  >
                    {matchCounts[t.key as StorefrontListTab]} in {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
          {/* Phones: one card per store (the table scrolled sideways inside
              its card, with every column after State off screen). */}
          <ul className={cn('sm:hidden flex flex-col transition-opacity', isFetching && 'opacity-70')}>
            {items.map((row) => (
              <StoreCard key={row.id} row={row} />
            ))}
          </ul>
          <div className={cn('hidden sm:block overflow-x-auto transition-opacity', isFetching && 'opacity-70')}>
            {/* Fixed columns so the table fits from a 1280px window: markers
                sit in the State column, dates stack date over time, long
                owner emails truncate (full address on hover). */}
            <table className="w-full min-w-[80rem] table-fixed">
              <colgroup>
                <col className="w-[24%]" />
                <col className="w-[24%]" />
                <col className="w-[16%]" />
                <col className="w-[7%]" />
                <col className="w-[8%]" />
                <col className="w-[7%]" />
                <col className="w-[7%]" />
                <col className="w-[7%]" />
              </colgroup>
              <thead className="bg-raised border-b border-line-subtle">
                <tr>
                  <SortTh label="Store" k="name" sort={effectiveSort} onSort={toggleSort} />
                  <Th>State</Th>
                  <Th>Owner</Th>
                  <SortTh label="Products" k="products" sort={effectiveSort} onSort={toggleSort} align="right" />
                  <SortTh label="Customers" k="customers" sort={effectiveSort} onSort={toggleSort} align="right" />
                  <SortTh label="Submitted" k="submitted_at" sort={effectiveSort} onSort={toggleSort} />
                  <SortTh label="Reviewed" k="reviewed_at" sort={effectiveSort} onSort={toggleSort} />
                  <SortTh label="Last edit" k="updated_at" sort={effectiveSort} onSort={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <StoreRow
                    key={row.id}
                    row={row}
                    onOpen={() => navigate(`/admin/storefronts/${encodeURIComponent(row.id)}`)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
      <p className="type-body-sm text-ink-muted">
        Sales, orders and visits per store are on each store&apos;s page.
      </p>
    </div>
  );
}

function SortTh({
  label,
  k,
  sort,
  onSort,
  align = 'left',
}: {
  label: string;
  k: SortKey;
  sort: Sort;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = sort?.key === k;
  return (
    <Th align={align}>
      <button
        type="button"
        onClick={() => onSort(k)}
        aria-sort={active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : undefined}
        className={cn(
          'inline-flex items-center gap-4 h-36 type-label-xs hover:text-ink',
          active ? 'text-ink' : 'text-ink-muted',
        )}
      >
        {label}
        {active &&
          (sort?.dir === 'asc' ? (
            <ArrowUp size={11} aria-hidden />
          ) : (
            <ArrowDown size={11} aria-hidden />
          ))}
      </button>
    </Th>
  );
}

/** "Oct 22" over "3:04 PM" (the year only when it is not this year). */
function StackedDate({ iso }: { iso: string | null | undefined }) {
  const label = shortDateTime(iso);
  if (label === '—') return <span className="text-ink-muted">—</span>;
  const [date, time] = label.split(/,\s(?=\d{1,2}:\d{2})/);
  return (
    <span className="flex flex-col leading-130" title={label}>
      <span>{date}</span>
      {time && <span className="text-ink-muted">{time}</span>}
    </span>
  );
}

/** The markers in a row's State cell (and a phone card): status, review
 *  state, waiting time, edits and photos since approval, rule issues. */
function StateMarkers({ row }: { row: AdminStorefrontListItem }) {
  // The detail page reads the same baseline; a change only waiting for
  // review is listed under Changes pending, not as an edit.
  const editedSinceApproval =
    row.review_status !== 'changes_pending_review' && isEditedSinceApproval(row);
  const notServed = liveButNotServed(row);
  const problems = storeProblems(row);
  const waiting = row.review_status === 'submitted' ? ageLabel(row.submitted_at) : null;
  const edits = row.edits_since_review ?? 0;
  const published = row.published_since_review ?? 0;
  return (
    <div className="flex flex-wrap items-center gap-6">
      {notServed ? (
        <Pill tone="danger" dot>
          Live · not served
        </Pill>
      ) : (
        <Pill tone={STATUS_TONE[row.status] ?? 'neutral'} dot>
          {STATUS_LABEL[row.status] ?? row.status}
        </Pill>
      )}
      {!(row.status === 'live' && row.review_status === 'approved') && (
        <Pill tone={REVIEW_TONE[row.review_status] ?? 'neutral'}>
          {REVIEW_LABEL[row.review_status] ?? row.review_status}
        </Pill>
      )}
      {waiting && (
        <Pill tone={waitTone(row.submitted_at)} className="normal-case">
          waiting {waiting}
        </Pill>
      )}
      {editedSinceApproval && (
        <Pill tone="warning" className="normal-case">
          Edited since approval{edits > 0 ? ` · ${edits} edit${edits === 1 ? '' : 's'}` : ''}
        </Pill>
      )}
      {published > 0 && (
        <Pill tone="info" className="normal-case">
          Published without review{published > 1 ? ` · ${published}×` : ''}
        </Pill>
      )}
      {hasPolicy(row) && <ReviewPolicyPill policy={reviewPolicyOf(row)} />}
      {row.media_changed_since_review && (
        <Pill tone="info" className="normal-case">
          <ImageIcon size={11} aria-hidden /> New photos
        </Pill>
      )}
      {problems.length > 0 && (
        <span
          className="inline-flex items-center gap-4 type-label-xs text-warning"
          title={problems.map((p) => p.message).join('\n')}
        >
          <AlertTriangle size={12} aria-hidden />
          {problems.length === 1 ? '1 rule issue' : `${problems.length} rule issues`}
        </span>
      )}
    </div>
  );
}

function StoreName({ row }: { row: AdminStorefrontListItem }) {
  return (
    <div className="flex items-center gap-12 min-w-0">
      <StoreMark name={row.name} logoUrl={row.logo_url} accent={row.accent_color} />
      <div className="flex flex-col gap-4 min-w-0">
        <Link
          to={`/admin/storefronts/${encodeURIComponent(row.id)}`}
          onClick={(e) => e.stopPropagation()}
          className="type-body-md text-ink hover:underline truncate"
        >
          {row.name}
        </Link>
        <span className="font-mono text-11 text-ink-muted truncate">
          {row.slug}.{STORE_ROOT_DOMAIN}
        </span>
      </div>
    </div>
  );
}

/** A store on a phone: name, state markers, owner, products and the last
 *  edit, stacked. The name is the link. */
function StoreCard({ row }: { row: AdminStorefrontListItem }) {
  return (
    <li className="flex flex-col gap-10 px-12 py-12 border-b border-line-subtle last:border-b-0">
      <StoreName row={row} />
      <StateMarkers row={row} />
      <dl className="grid grid-cols-2 gap-x-12 gap-y-6 type-body-sm">
        <div className="col-span-2 min-w-0">
          <dt className="type-label-xs text-ink-muted">Owner</dt>
          <dd className="text-ink-secondary break-all">{row.owner_email ?? '—'}</dd>
        </div>
        <div>
          <dt className="type-label-xs text-ink-muted">Products</dt>
          <dd className="text-ink-secondary">{count(row.products_count)}</dd>
        </div>
        <div>
          <dt className="type-label-xs text-ink-muted">Customers</dt>
          <dd className="text-ink-secondary">{count(row.customers_count)}</dd>
        </div>
        <div>
          <dt className="type-label-xs text-ink-muted">Last edit</dt>
          <dd className="text-ink-secondary">{shortDateTime(row.updated_at)}</dd>
        </div>
      </dl>
    </li>
  );
}

function StoreRow({ row, onOpen }: { row: AdminStorefrontListItem; onOpen: () => void }) {
  return (
    <tr
      onClick={onOpen}
      className="border-b border-line-subtle last:border-b-0 cursor-pointer hover:bg-raised"
    >
      <Td>
        <StoreName row={row} />
      </Td>
      <Td>
        <StateMarkers row={row} />
      </Td>
      <Td className="text-ink-secondary">
        <span className="block truncate" title={row.owner_email ?? undefined}>
          {row.owner_email ?? '—'}
        </span>
      </Td>
      <Td align="right">{count(row.products_count)}</Td>
      <Td align="right">{count(row.customers_count)}</Td>
      <Td className="text-ink-secondary">
        <StackedDate iso={row.submitted_at} />
      </Td>
      <Td className="text-ink-secondary">
        <StackedDate iso={row.reviewed_at} />
      </Td>
      <Td className="text-ink-secondary">
        <StackedDate iso={row.updated_at} />
      </Td>
    </tr>
  );
}
